/**
 * DepartmentSelect
 * ----------------
 * Department picker that accepts values outside the standard list — e.g.
 * "Executive Leadership - Supply Chain". Any custom value already on the
 * contact is shown as a selected option, so editing never silently drops it.
 */
import { useState } from "react";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pencil, Check, X } from "lucide-react";

export const HOSPITAL_DEPARTMENTS = [
  "Supply Chain", "Facilities Management", "Environmental Services (EVS)",
  "Biomedical Engineering (Bio Med)", "Sterile Processing (SPD)", "Nursing",
  "Administration", "Finance / Billing", "IT / Informatics", "Infection Prevention",
  "Operations", "Risk / Compliance", "Executive Leadership", "Other",
];

const CUSTOM = "__custom__";

export default function DepartmentSelect({ value, onChange, testId = "department-select" }) {
  const isCustom = !!value && !HOSPITAL_DEPARTMENTS.includes(value);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  const commit = () => {
    const v = draft.trim();
    if (v) onChange(v);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { setEditing(false); setDraft(value || ""); }
          }}
          placeholder="Executive Leadership - Supply Chain"
          className="h-9 text-sm"
          data-testid={`${testId}-custom-input`}
        />
        <Button type="button" size="sm" variant="ghost" className="h-9 px-2"
          onClick={commit} title="Use this department"
          data-testid={`${testId}-custom-save`}>
          <Check className="w-4 h-4 text-emerald-600" />
        </Button>
        <Button type="button" size="sm" variant="ghost" className="h-9 px-2"
          onClick={() => { setEditing(false); setDraft(value || ""); }} title="Cancel"
          data-testid={`${testId}-custom-cancel`}>
          <X className="w-4 h-4 text-slate-400" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Select
        value={value || ""}
        onValueChange={(v) => {
          if (v === CUSTOM) { setDraft(isCustom ? value : ""); setEditing(true); return; }
          onChange(v);
        }}
      >
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Select department" /></SelectTrigger>
        <SelectContent>
          {isCustom && (
            <SelectItem value={value} data-testid={`${testId}-current-custom`}>{value}</SelectItem>
          )}
          {HOSPITAL_DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          <SelectItem value={CUSTOM} data-testid={`${testId}-custom-option`}>
            + Type a custom department…
          </SelectItem>
        </SelectContent>
      </Select>
      {isCustom && (
        <Button type="button" size="sm" variant="ghost" className="h-9 px-2"
          onClick={() => { setDraft(value); setEditing(true); }} title="Edit this department title"
          data-testid={`${testId}-edit-custom`}>
          <Pencil className="w-3.5 h-3.5 text-slate-400" />
        </Button>
      )}
    </div>
  );
}
